import { createContext, FunctionComponent } from 'preact';
import { useCallback, useContext, useMemo, useRef } from 'preact/hooks';
import type { PulseStatsPlugin } from '@schibsted-svp/web-player';

import { usePlayer } from './PlayerProvider';

type Callback = (pulseStatsPlugin: PulseStatsPlugin) => void;

interface PulseContextProps {
  track: PulseStatsPlugin['track'];
  trackMediaEvent: PulseStatsPlugin['trackMediaEvent'];
}

const PulseContext = createContext<PulseContextProps>(undefined!);

export const PulseProvider: FunctionComponent = ({ children }) => {
  const player = usePlayer();

  const queue = useRef<Callback[]>([]);
  const isQueueBeingProcessed = useRef(false);

  const processQueue = useCallback(async () => {
    const pulseStatsPlugin = player.getPlugin('PulseStats');

    if (!pulseStatsPlugin || isQueueBeingProcessed.current) {
      return;
    }

    isQueueBeingProcessed.current = true;

    while (queue.current.length > 0) {
      try {
        const callback = queue.current.shift()!;

        await callback(pulseStatsPlugin);
      } catch (err) {
        console.error(err);
      }
    }

    isQueueBeingProcessed.current = false;
  }, [player]);

  const push = useCallback(
    (callback: Callback) => {
      queue.current.push(callback);

      return processQueue();
    },
    [processQueue]
  );

  const track = useCallback<PulseStatsPlugin['track']>(
    (...args) => {
      return push(async (pulseStatsPlugin) => {
        await pulseStatsPlugin.track(...args);
      });
    },
    [push]
  );

  const trackMediaEvent = useCallback<PulseStatsPlugin['trackMediaEvent']>(
    (...args) => {
      return push(async (pulseStatsPlugin) => {
        await pulseStatsPlugin.trackMediaEvent(...args);
      });
    },
    [push]
  );

  const state = useMemo(() => {
    return {
      track,
      trackMediaEvent,
    };
  }, [track, trackMediaEvent]);

  return (
    <PulseContext.Provider value={state}>{children}</PulseContext.Provider>
  );
};

export const usePulse = () => {
  const pulse = useContext(PulseContext);

  if (process.env.NODE_ENV !== 'production' && !pulse) {
    console.warn(
      'Pulse context is undefined, please verify you are calling usePulse() as child of a <PulseProvider> component.'
    );
  }

  return pulse;
};
